import type { Probe, Finding, ProbeResult } from '../engine/types';
import { ok } from '../engine/runner';
import { registerProbe } from '../engine/registry';
import { sig } from '../engine/normalize';

const HR_TIME_SOURCE = 'https://www.w3.org/TR/hr-time/';
const CHROME_TIMERS_SOURCE =
  'https://developer.chrome.com/blog/cross-origin-isolated-hr-timers/';
const COI_SOURCE = 'https://html.spec.whatwg.org/multipage/browsers.html#cross-origin-isolated';

/**
 * Timer-resolution / clamping probe (security-research §7, and the SAB
 * cross-check in the existing sab probe).
 *
 * The mechanism is Spectre-adjacent hardening. Since Chrome 91 the spec clamps
 * `performance.now()` (and `performance.timeOrigin`, and every other
 * DOMHighResTimestamp) to **100 µs** in a page that is NOT cross-origin
 * isolated, and relaxes it to **5 µs** when the page IS isolated. Firefox is
 * coarser still, and `privacy.resistFingerprinting` pushes it to ~1 ms.
 *
 * That makes the observed granularity a two-part signal:
 *   - which engine / privacy configuration is this?
 *   - is COOP/COEP isolation *genuinely granted*, or merely requested?
 *
 * We measure the granularity empirically rather than reading a constant: sample
 * many successive `performance.now()` values, keep the smallest non-zero
 * differences, and report the modal quantum. That is the number the browser
 * actually quantises to.
 *
 * Nothing here is scored. A privacy browser deliberately poisons timing, so a
 * coarse value is a legitimate configuration, not a bot signal — the value is
 * reported and hashed (it is a stable engine property) but contributes no
 * score on its own.
 */
export const timerResolutionProbe: Probe = {
  id: 'timerResolution',
  label: 'Timer resolution',
  layer: 'engine',
  tier: 'client',
  category: 'hardware',
  description: 'performance.now() clamping granularity and cross-origin-isolation coupling',
  sources: [HR_TIME_SOURCE, CHROME_TIMERS_SOURCE, COI_SOURCE],
  budgetMs: 3000,

  async run(): Promise<ProbeResult> {
    const findings: Finding[] = [];
    const isolated = (globalThis as { crossOriginIsolated?: boolean }).crossOriginIsolated === true;

    // --- 1. performance.now() quantum -------------------------------------
    // To observe the clock's QUANTUM we must read it as fast as possible: the
    // quantum is the smallest step the timer ever takes. Sleeping between reads
    // (a previous bug here) would just measure our own sleep interval, so we
    // spin-read in a tight loop until the value changes, recording every
    // distinct value we manage to catch.
    const nowSamples: number[] = [];
    const deadline = performance.now() + 30;
    let last = performance.now();
    nowSamples.push(last);
    // A tight loop, bounded by wall clock so it cannot spin forever.
    while (performance.now() < deadline && nowSamples.length < 5000) {
      const t = performance.now();
      if (t !== last) {
        nowSamples.push(t);
        last = t;
      }
    }
    // The deltas between consecutive DISTINCT values are the observable steps.
    // Under a clamp, the minimum such step is the quantum; the modal one is the
    // typical step the clock advances by between our reads.
    const deltas: number[] = [];
    for (let i = 1; i < nowSamples.length; i++) {
      const d = nowSamples[i] - nowSamples[i - 1];
      if (d > 0) deltas.push(d);
    }
    const quantumMs = smallestStep(deltas);
    // Convert the quantum to microseconds, rounded to the plausible range.
    const quantumUs = quantumMs == null ? null : sig(quantumMs * 1000, 2);

    // --- 2. Date.now() as a coarse reference -------------------------------
    // Date.now() is intentionally 1 ms-granular, so it should never be finer
    // than performance.now(); if it is, something is lying.
    const dateQuantumUs = measureDateQuantumUs();

    // --- 3. SharedArrayBuffer-backed clock, when isolated ------------------
    // A SAB read is a true memory read (not a clamped timer), so comparing its
    // observed granularity against performance.now() is the direct test of
    // whether isolation is really in force.
    let sabPresent = false;
    let sabQuantumUs: number | null = null;
    if (isolated && typeof SharedArrayBuffer === 'function') {
      try {
        const sab = new SharedArrayBuffer(8);
        const view = new Float64Array(sab);
        // performance.now() is the only clock available in a page, so rather
        // than inventing a second quantum we time a tight SAB write/read loop
        // and confirm the primitive is genuinely usable in this context. A
        // "randomised" guard value keeps V8 from optimising the loop away.
        const t0 = performance.now();
        let guard = 0;
        for (let i = 0; i < 1000; i++) {
          view[0] = i;
          guard += view[0];
        }
        const elapsed = performance.now() - t0;
        sabPresent = guard >= 0; // SAB round trip actually read back
        // If performance.now() is clamped to ~100 µs, a sub-quantum loop still
        // advances by at least one quantum. Reported, never scored.
        sabQuantumUs = sig(Math.max(0, elapsed) * 1000, 2);
      } catch {
        sabPresent = false;
      }
    }

    // --- coherence finding: isolation claimed but timers coarse -----------
    // On a conformant Chromium, an isolated page should show ~5 µs and a
    // non-isolated page ~100 µs. If isolation is granted but the timer is at
    // the coarse (non-isolated) resolution, the security context is not what
    // it claims — a sign the page is being served or embedded unexpectedly.
    if (isolated && quantumUs != null && quantumUs >= 50) {
      findings.push({
        id: 'timer.isolated-but-coarse',
        severity: 'medium',
        category: 'hardware',
        layer: 'engine',
        confidence: 'medium',
        title: 'Cross-origin isolated, but performance.now() is at the coarse clamp',
        detail:
          'A conformant Chromium relaxes performance.now() to ~5 µs when a page is cross-origin isolated, and keeps it at ~100 µs when it is not. Observing the coarse clamp in a page that reports crossOriginIsolated === true means the security context is not behaving as advertised — which can happen when a page is embedded or framed in a way that changes the isolation state, or when an automation stack sets the headers but the environment does not honour them.',
        sources: [CHROME_TIMERS_SOURCE, COI_SOURCE],
        evidence: [
          `crossOriginIsolated=${isolated}`,
          `performance.now quantum ≈ ${quantumUs} µs`,
        ],
        scoreContribution: 15,
      });
    }

    return {
      signals: {
        // Reported for context; the quantum is the real measurement.
        crossOriginIsolated: ok(isolated, 'client'),
        sampleCount: ok(nowSamples.length, 'client'),
        minDeltaMs: ok(deltas.length ? sig(Math.min(...deltas), 4) : null, 'derived'),
        medianDeltaMs: ok(deltas.length ? sig(median(deltas), 4) : null, 'derived'),
        // The headline value: the quantum performance.now() is clamped to.
        quantumMs: ok(quantumMs == null ? null : sig(quantumMs, 4), 'derived'),
        quantumUs: ok(quantumUs, 'derived'),
        quantumBucket: ok(bucketQuantum(quantumUs), 'derived'),
        // Date.now() is spec'd to be 1 ms; used only as a sanity reference.
        dateQuantumUs: ok(dateQuantumUs, 'derived'),
        // SAB presence + whether it is coupled to isolation as the spec requires.
        sharedArrayBufferPresent: ok(sabPresent, 'client'),
        sabCoupledToIsolation: ok(isolated === sabPresent, 'derived'),
        sabLoopUs: ok(sabQuantumUs, 'client'),
      },
      findings,
      parts: {
        engineTiming: {
          // The clamped quantum is a stable engine + isolation property, so it
          // belongs in the fingerprint. The raw sample timing does not.
          timerQuantumBucket: bucketQuantum(quantumUs),
          crossOriginIsolated: isolated,
        },
        sab: {
          sabCoupledToIsolation: isolated === sabPresent,
        },
      },
    };
  },
};

/**
 * The smallest observable step the clock takes — its QUANTUM.
 *
 * We deliberately do NOT use the modal (most common) delta: in a tight spin
 * loop the dominant delta reflects how fast the loop can iterate, not the
 * clock's clamping. The clamp shows up as the floor: the clock never advances
 * by less than the quantum, and does advance by it when we read fast enough.
 */
function smallestStep(deltas: number[]): number | null {
  if (!deltas.length) return null;
  // Quantise to 1 ns before taking the floor, so float representation error
  // (performance.now() is a float64 ms value) cannot report a sub-ns step.
  let minKey = Number.POSITIVE_INFINITY;
  for (const d of deltas) {
    const key = Math.round(d * 1e6); // ns
    if (key > 0 && key < minKey) minKey = key;
  }
  if (!Number.isFinite(minKey)) return null;
  return minKey / 1e6;
}

function median(xs: number[]): number {
  const s = [...xs].sort((a, b) => a - b);
  return s[Math.floor(s.length / 2)];
}

/** Date.now() is spec'd to 1 ms granularity; confirm nothing finer leaks. */
function measureDateQuantumUs(): number | null {
  const samples: number[] = [];
  for (let i = 0; i < 20; i++) {
    const t = Date.now();
    if (samples.length === 0 || t !== samples[samples.length - 1]) samples.push(t);
  }
  const deltas: number[] = [];
  for (let i = 1; i < samples.length; i++) {
    const d = samples[i] - samples[i - 1];
    if (d > 0) deltas.push(d);
  }
  return deltas.length ? Math.min(...deltas) * 1000 : null;
}

/**
 * Map an observed quantum to a human bucket. These are the published figures:
 * ~5 µs (isolated Chromium), ~100 µs (non-isolated Chromium since v91),
 * ~1000 µs (Firefox / privacy.resistFingerprinting).
 */
function bucketQuantum(quantumUs: number | null): string {
  if (quantumUs == null) return 'unknown';
  if (quantumUs <= 10) return 'high-res (~5us, isolated)';
  if (quantumUs <= 200) return 'coarse (~100us, non-isolated)';
  return 'very-coarse (>=1ms, privacy-hardened)';
}

registerProbe(timerResolutionProbe);
